import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { de, type Dictionary } from "./de";
import { en } from "./en";
import type { Localized } from "../config/site";

export type Lang = "de" | "en";

// Weitere Sprachen (z. B. "fa" für Persisch) hier registrieren und eine Datei wie en.ts anlegen.
export const languages: { code: Lang; label: string; nativeName: string; complete: boolean }[] = [
  { code: "de", label: "DE", nativeName: "Deutsch", complete: true },
  { code: "en", label: "EN", nativeName: "English", complete: true },
];

const dictionaries: Record<Lang, Dictionary> = { de, en };

type Ctx = { lang: Lang; t: Dictionary; setLang: (l: Lang) => void; tr: (v: Localized) => string };

const LanguageContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "kp-lang";

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = typeof window !== "undefined" ? (localStorage.getItem(STORAGE_KEY) as Lang | null) : null;
    return saved && dictionaries[saved] ? saved : "de";
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<Ctx>(() => ({ lang, t: dictionaries[lang], setLang, tr: (v) => v[lang] ?? v.de }), [lang, setLang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
